import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getBotStoreCurrent, getBotStoreList, postAiTrainingScenarioPage } from '@/common/api/bot';
import {
  TrainingPhase,
  TrainingRecord,
  TrainingScene,
} from '../pages/training/data';

export interface BotStore {
  id: number;
  storeName: string;
  storeCode: string;
  status: number;
  bound: boolean;
}

export interface AbsideState {
  number: number;
  coachScenes: TrainingScene[];
  coachRecords: TrainingRecord[];
  sessionRecords: TrainingRecord[];
  currentSceneId?: string;
  currentRecordId?: string;
  recordingDuration?: number;
  thinkingType?: 'question' | 'analysis';
  coachPhase: TrainingPhase;
  storeList: BotStore[];
  currentStore?: BotStore;
  currentStoreLoaded: boolean;
  customerListNeedRefresh: boolean;
}

const initialState: AbsideState = {
  number: 0,
  coachScenes: [],
  coachRecords: [],
  sessionRecords: [],
  coachPhase: 'idle',
  storeList: [],
  currentStoreLoaded: false,
  customerListNeedRefresh: false,
};

export const fetchBotStoreList = createAsyncThunk('abside/fetchBotStoreList', async () => {
  const res = await getBotStoreList();
  const data = res && typeof res === 'object' && 'data' in res ? res.data : res;

  return Array.isArray(data) ? data as BotStore[] : [];
});

export const fetchBotStoreCurrent = createAsyncThunk('abside/fetchBotStoreCurrent', async () => {
  const res = await getBotStoreCurrent();
  const data = res && typeof res === 'object' && 'data' in res ? res.data : res;

  if (!data || typeof data !== 'object' || !data.id || data.bound === false) {
    return undefined;
  }

  return data as BotStore;
});

export const fetchAiTrainingScenes = createAsyncThunk('abside/fetchAiTrainingScenes', async () => {
  const res = await postAiTrainingScenarioPage({
    page: 1,
    size: 10,
    status: 1,
  });
  const data = res && typeof res === 'object' ? res : {};
  const scenarios = Array.isArray(data.data) ? data.data : [];

  return scenarios
    .filter(item => item?.status === 1)
    .map(item => ({
      id: String(item.id),
      name: item.name || '',
      desc: item.description || '',
      status: 'on' as const,
    }))
    .filter(item => item.name);
});

const absideSlice = createSlice({
  name: 'abside',
  initialState,
  reducers: {
    changeCount: (state, action: PayloadAction<number>) => {
      state.number = action.payload;
    },
    selectScene: (state, action: PayloadAction<string>) => {
      state.currentSceneId = action.payload;
      state.currentRecordId = undefined;
      state.recordingDuration = undefined;
      state.thinkingType = 'question';
      state.coachPhase = 'thinking';
    },
    showQuestion: (state) => {
      state.thinkingType = undefined;
      state.coachPhase = 'question';
    },
    startRecording: (state) => {
      state.coachPhase = 'recording';
    },
    startAnalyzing: (state, action: PayloadAction<number>) => {
      state.recordingDuration = action.payload;
      state.thinkingType = undefined;
      state.coachPhase = 'analyzing';
    },
    startThinking: (state, action: PayloadAction<number>) => {
      state.recordingDuration = action.payload;
      state.thinkingType = 'analysis';
      state.coachPhase = 'thinking';
    },
    completeTraining: (state, action: PayloadAction<TrainingRecord>) => {
      state.coachRecords = [action.payload, ...state.coachRecords];
      state.sessionRecords = [...state.sessionRecords, action.payload];
      state.currentRecordId = action.payload.id;
      state.thinkingType = undefined;
      state.coachPhase = 'result';
    },
    setCoachRecords: (state, action: PayloadAction<TrainingRecord[]>) => {
      state.coachRecords = action.payload;
    },
    appendCoachRecords: (state, action: PayloadAction<TrainingRecord[]>) => {
      const existingRecordIds = new Set(state.coachRecords.map(item => item.id));
      state.coachRecords.push(...action.payload.filter(item => !existingRecordIds.has(item.id)));
    },
    setCurrentRecord: (state, action: PayloadAction<string>) => {
      state.currentRecordId = action.payload;
    },
    resetTraining: (state) => {
      state.currentSceneId = undefined;
      state.currentRecordId = undefined;
      state.recordingDuration = undefined;
      state.thinkingType = undefined;
      state.sessionRecords = [];
      state.coachPhase = 'idle';
    },
    setCurrentStore: (state, action: PayloadAction<BotStore | undefined>) => {
      state.currentStore = action.payload;
      state.currentStoreLoaded = true;
    },
    markCustomerListNeedRefresh: (state) => {
      state.customerListNeedRefresh = true;
    },
    clearCustomerListNeedRefresh: (state) => {
      state.customerListNeedRefresh = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchBotStoreList.fulfilled, (state, action) => {
        state.storeList = action.payload;
      })
      .addCase(fetchBotStoreCurrent.pending, (state) => {
        state.currentStoreLoaded = false;
      })
      .addCase(fetchBotStoreCurrent.fulfilled, (state, action) => {
        state.currentStore = action.payload;
        state.currentStoreLoaded = true;
      })
      .addCase(fetchBotStoreCurrent.rejected, (state) => {
        state.currentStoreLoaded = true;
      })
      .addCase(fetchAiTrainingScenes.fulfilled, (state, action) => {
        state.coachScenes = action.payload;
      })
      .addCase(fetchAiTrainingScenes.rejected, (state) => {
        state.coachScenes = [];
      });
  },
});

export const {
  changeCount,
  appendCoachRecords,
  clearCustomerListNeedRefresh,
  completeTraining,
  markCustomerListNeedRefresh,
  resetTraining,
  selectScene,
  setCurrentStore,
  setCoachRecords,
  showQuestion,
  setCurrentRecord,
  startAnalyzing,
  startRecording,
  startThinking,
} = absideSlice.actions;

export default absideSlice.reducer;
